/**
 * Risk Calculation Service
 *
 * Handles risk score calculations and ALARP logic.
 *
 * @module services/riskCalculationService
 */

import { RISK_LEVELS, ALARP_ZONES, CONTROL_HIERARCHY, LIKELIHOOD_SCALE, SEVERITY_SCALE } from '../config/moduleConfig';

/**
 * Risk Calculation Service
 * Provides risk scoring and ALARP logic
 */
export const riskCalculationService = {

  /**
   * Calculate risk score from likelihood and severity
   * @param {number} likelihood - Likelihood value (1-5)
   * @param {number} severity - Severity value (1-5)
   * @returns {number} Risk score (1-25)
   */
  calculateRiskScore: (likelihood, severity) => {
    const l = Math.min(5, Math.max(1, parseInt(likelihood) || 1));
    const s = Math.min(5, Math.max(1, parseInt(severity) || 1));
    return l * s;
  },

  /**
   * Get risk level from score
   * @param {number} score - Risk score (1-25)
   * @returns {string} Risk level key (extreme, high, medium, low)
   */
  getRiskLevel: (score) => {
    if (score >= 20) return 'extreme';
    if (score >= 12) return 'high';
    if (score >= 6) return 'medium';
    return 'low';
  },

  /**
   * Get risk level details
   * @param {number} score - Risk score (1-25)
   * @returns {Object} Risk level details
   */
  getRiskLevelDetails: (score) => {
    const level = riskCalculationService.getRiskLevel(score);
    return {
      key: level,
      ...RISK_LEVELS[level]
    };
  },

  /**
   * Get ALARP zone from score
   * @param {number} score - Risk score (1-25)
   * @returns {string} ALARP zone key (intolerable, alarp, acceptable)
   */
  getAlarpZone: (score) => {
    if (score >= 20) return 'intolerable';
    if (score >= 6) return 'alarp';
    return 'acceptable';
  },

  /**
   * Get ALARP zone details
   * @param {number} score - Risk score (1-25)
   * @returns {Object} ALARP zone details
   */
  getAlarpZoneDetails: (score) => {
    const zone = riskCalculationService.getAlarpZone(score);
    return {
      key: zone,
      ...ALARP_ZONES[zone]
    };
  },

  /**
   * Calculate residual risk based on controls
   * Estimates how controls reduce risk
   * @param {Object} hazard - Hazard object with initial values
   * @param {Array} controls - Array of control objects
   * @returns {Object} Calculated residual values
   */
  calculateResidualRisk: (hazard, controls = []) => {
    let likelihoodReduction = 0;
    let severityReduction = 0;

    // Calculate reduction based on control effectiveness
    controls.forEach(control => {
      const effectiveness = riskCalculationService.getControlEffectiveness(control.type);
      likelihoodReduction += effectiveness.likelihoodReduction || 0;
      severityReduction += effectiveness.severityReduction || 0;
    });

    // Apply reductions (minimum value is 1)
    const residualLikelihood = Math.max(1, hazard.initialLikelihood - likelihoodReduction);
    const residualSeverity = Math.max(1, hazard.initialSeverity - severityReduction);
    const residualRiskScore = riskCalculationService.calculateRiskScore(residualLikelihood, residualSeverity);

    return {
      residualLikelihood,
      residualSeverity,
      residualRiskScore,
      riskLevel: riskCalculationService.getRiskLevel(residualRiskScore),
      alarpZone: riskCalculationService.getAlarpZone(residualRiskScore),
      reductionPercentage: hazard.initialRiskScore > 0
        ? Math.round(((hazard.initialRiskScore - residualRiskScore) / hazard.initialRiskScore) * 100)
        : 0
    };
  },

  /**
   * Get control effectiveness based on hierarchy level
   * @param {string} controlType - Control hierarchy type
   * @returns {Object} Effectiveness data
   */
  getControlEffectiveness: (controlType) => {
    const effectivenessMap = {
      elimination: { likelihoodReduction: 4, severityReduction: 4, score: 5 },
      substitution: { likelihoodReduction: 3, severityReduction: 3, score: 4 },
      engineering: { likelihoodReduction: 2, severityReduction: 2, score: 3 },
      administrative: { likelihoodReduction: 1, severityReduction: 1, score: 2 },
      ppe: { likelihoodReduction: 1, severityReduction: 0, score: 1 }
    };

    return effectivenessMap[controlType] || effectivenessMap.ppe;
  },

  /**
   * Validate risk assessment data
   * @param {Object} assessment - Risk assessment object
   * @returns {Object} Validation result { valid: boolean, errors: string[], warnings: string[] }
   */
  validateRiskAssessment: (assessment) => {
    const errors = [];
    const warnings = [];

    // Required fields
    if (!assessment.title || assessment.title.trim() === '') {
      errors.push('Title is required');
    }

    if (!assessment.type) {
      errors.push('Assessment type is required');
    }

    // Hazards validation
    if (!assessment.hazards || assessment.hazards.length === 0) {
      errors.push('At least one hazard must be identified');
    } else {
      assessment.hazards.forEach((hazard, index) => {
        const hazardNum = index + 1;

        if (!hazard.hazardDescription || hazard.hazardDescription.trim() === '') {
          errors.push(`Hazard ${hazardNum}: Description is required`);
        }

        if (hazard.initialLikelihood < 1 || hazard.initialLikelihood > 5) {
          errors.push(`Hazard ${hazardNum}: Invalid likelihood value (must be 1-5)`);
        }

        if (hazard.initialSeverity < 1 || hazard.initialSeverity > 5) {
          errors.push(`Hazard ${hazardNum}: Invalid severity value (must be 1-5)`);
        }

        // Warnings for high-risk without action
        const alarpZone = riskCalculationService.getAlarpZone(hazard.residualRiskScore);
        if (alarpZone === 'intolerable' && !hazard.actionRequired) {
          warnings.push(`Hazard ${hazardNum}: Intolerable risk requires an action plan`);
        }

        if (alarpZone === 'alarp' && !hazard.existingControls && !hazard.additionalControls) {
          warnings.push(`Hazard ${hazardNum}: ALARP risk should have controls documented`);
        }

        // Warning if residual risk is higher than initial
        if (hazard.residualRiskScore > hazard.initialRiskScore) {
          warnings.push(`Hazard ${hazardNum}: Residual risk is higher than initial risk`);
        }
      });
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings
    };
  },

  /**
   * Generate risk matrix data for visualization
   * @returns {Array} 5x5 matrix data
   */
  generateRiskMatrix: () => {
    const matrix = [];

    for (let severity = 5; severity >= 1; severity--) {
      const row = [];
      for (let likelihood = 1; likelihood <= 5; likelihood++) {
        const score = riskCalculationService.calculateRiskScore(likelihood, severity);
        const level = riskCalculationService.getRiskLevel(score);

        row.push({
          likelihood,
          severity,
          score,
          level,
          color: RISK_LEVELS[level].bgColor,
          textColor: RISK_LEVELS[level].textColor
        });
      }
      matrix.push(row);
    }

    return matrix;
  },

  /**
   * Get likelihood scale for display
   * @returns {Array} Likelihood options
   */
  getLikelihoodScale: () => {
    return Object.values(LIKELIHOOD_SCALE).map(item => ({
      value: item.value,
      label: item.label,
      description: item.description,
      frequency: item.frequency
    }));
  },

  /**
   * Get severity scale for display
   * @returns {Array} Severity options
   */
  getSeverityScale: () => {
    return Object.values(SEVERITY_SCALE).map(item => ({
      value: item.value,
      label: item.label,
      description: item.description,
      impact: item.impact,
      medicalTreatment: item.medicalTreatment
    }));
  },

  /**
   * Get control hierarchy for display
   * @returns {Array} Control hierarchy options
   */
  getControlHierarchy: () => {
    return Object.entries(CONTROL_HIERARCHY).map(([key, item]) => ({
      key,
      level: item.level,
      label: item.label,
      description: item.description,
      examples: item.examples,
      effectiveness: item.effectiveness,
      icon: item.icon
    }));
  },

  /**
   * Calculate overall assessment risk score
   * @param {Array} hazards - Array of hazard objects
   * @returns {Object} Overall risk data
   */
  calculateOverallRisk: (hazards = []) => {
    if (hazards.length === 0) {
      return {
        initialRiskScore: 0,
        residualRiskScore: 0,
        riskLevel: 'low',
        alarpZone: 'acceptable',
        averageRiskReduction: 0
      };
    }

    // Overall risk is the HIGHEST risk from any hazard
    const initialRiskScore = Math.max(...hazards.map(h => h.initialRiskScore || 0));
    const residualRiskScore = Math.max(...hazards.map(h => h.residualRiskScore || 0));

    // Calculate average risk reduction
    const reductions = hazards.map(h => {
      if (h.initialRiskScore === 0) return 0;
      return ((h.initialRiskScore - h.residualRiskScore) / h.initialRiskScore) * 100;
    });
    const averageRiskReduction = Math.round(
      reductions.reduce((sum, val) => sum + val, 0) / reductions.length
    );

    return {
      initialRiskScore,
      residualRiskScore,
      riskLevel: riskCalculationService.getRiskLevel(residualRiskScore),
      alarpZone: riskCalculationService.getAlarpZone(residualRiskScore),
      averageRiskReduction
    };
  },

  /**
   * Get recommended review frequency based on risk level
   * @param {number} score - Risk score (1-25)
   * @returns {string} Recommended review frequency
   */
  getRecommendedReviewFrequency: (score) => {
    const level = riskCalculationService.getRiskLevel(score);

    switch (level) {
      case 'extreme':
        return 'monthly';
      case 'high':
        return 'quarterly';
      case 'medium':
        return 'biannually';
      case 'low':
      default:
        return 'annually';
    }
  },

  /**
   * Check if risk is acceptable
   * @param {number} score - Risk score (1-25)
   * @returns {boolean} True if in acceptable zone
   */
  isRiskAcceptable: (score) => {
    return riskCalculationService.getAlarpZone(score) === 'acceptable';
  },

  /**
   * Check if work can proceed
   * @param {number} score - Risk score (1-25)
   * @returns {boolean} True if work can proceed
   */
  canWorkProceed: (score) => {
    return riskCalculationService.getAlarpZone(score) !== 'intolerable';
  },

  /**
   * Get action required message based on risk level
   * @param {number} score - Risk score (1-25)
   * @returns {string} Action required message
   */
  getActionRequired: (score) => {
    const level = riskCalculationService.getRiskLevel(score);
    return RISK_LEVELS[level].action;
  }
};
